import type { WireChatAgent, WireChatListing } from '@/api/hooks';
import type { WirePerson, WireTwinPlanAnswer } from '@/api/wire';
import { toolName } from '@/app/technical';
import { agoWords, dayOf, firstOf, nowOf, others, plural, type Viewer } from '@/directory/explainKit';
import type { ItemContext, Party } from '@/ui/context';
import type { Who } from '@/ui/who';

/**
 * What the side chat's lists and cards say about themselves (ADR-0155):
 * the colleagues in the Team tab, the chats already had, the agents and
 * the twin one may talk to, and a twin's plan before anything runs. The chat
 * is the person's own and the twin prepares and never sends, so every tip
 * says who acts: the person reads and sends, the twin drafts, and a
 * department's agent proposes what a person decides.
 */

// --- the Team tab ------------------------------------------------------------------------------

export function explainColleague(p: WirePerson, extra: { lastAt: string | null; unread: boolean; onTalk: () => void }, who: Who, v: Viewer): ItemContext {
  const now = nowOf(v);
  const first = firstOf(p.displayName);
  const departments = p.departments.map(who.label);
  const leads = p.leads.map(who.label);
  const next = ((): ItemContext['next'] => {
    const actions = [{ label: `Open the conversation with ${first}`, run: extra.onTalk, primary: true }];
    if (extra.unread) {
      return { tip: `${first} wrote to you and you have not opened it. Read it first; a reply is yours to write, and your twin can prepare a draft for you to read and send.`, instead: 'Or ignore it for now: it stays marked as new until you open it.', actions };
    }
    if (extra.lastAt) {
      return { tip: `Nothing is needed. You last talked ${agoWords(extra.lastAt, now)}; open it to carry on or to read what was said.`, instead: 'To ask something of their department instead, make it a request from Requests: it confers nothing the department does not already hold.', actions };
    }
    return {
      tip: `You have not talked to ${first} yet. Open a conversation when you have something to ask: it is theirs and yours alone, and your twin can draft a message for you to read and send.`,
      instead: 'If it is a question for the whole department, a request to it reaches the right person without your having to know who.',
      actions,
    };
  })();
  return {
    subject: { kind: 'colleague', title: p.displayName },
    summary: `${p.displayName} works in ${departments.length ? departments.join(' and ') : 'no department yet'}${leads.length ? ` and leads ${leads.join(' and ')}` : ''}.`,
    need: extra.unread ? 'Your reading of what they wrote.' : 'Nothing.',
    why: 'The Team tab lists everyone who works here, so you can reach a person without knowing where they sit. A conversation is the two of yours and nobody else’s.',
    source: null,
    broughtBy: null,
    knows: others(p.departments.map((d) => who.lead(d))),
    facts: [
      ['Last talked', extra.lastAt ? agoWords(extra.lastAt, now) : 'Not yet'],
      ['Waiting for you', extra.unread ? 'Something new' : null],
    ],
    next,
  };
}

// --- past chats ---------------------------------------------------------------------------------

export function explainChatListing(l: WireChatListing, who: Who, v: Viewer): ItemContext {
  const now = nowOf(v);
  const withWhom = l.agentName ?? 'your twin';
  const agent: Party = l.agentId ? { name: `${l.agentName ?? 'An agent'} Agent`, kind: 'agent' } : { name: l.agentName ?? 'Your twin', kind: 'twin' };
  const last = l.lastTurnAt ?? l.startedAt;
  return {
    subject: { kind: 'chat', title: l.title ?? 'A chat with nothing said yet' },
    summary: `A chat with ${withWhom}${l.title ? `, begun with “${l.title}”` : ' with nothing said yet'}. Started ${dayOf(l.startedAt)}, last spoken in ${agoWords(last, now)}${l.closedAt ? `, and closed ${agoWords(l.closedAt, now)}` : ''}.`,
    need: 'Nothing.',
    why: 'Every chat is kept so you can come back to it. Only you can read it: no other person and no department can.',
    source: { label: l.title ? 'You started it by asking: “' + l.title + '”' : 'You opened it and did not say anything', when: dayOf(l.startedAt) ?? undefined },
    broughtBy: { ...who.me, when: dayOf(l.startedAt) ?? undefined },
    owner: who.me,
    involved: [agent],
    facts: [
      ['Started', dayOf(l.startedAt)],
      ['Last spoken', agoWords(last, now)],
      ['State', l.closedAt ? 'Closed: read only' : 'Open'],
    ],
    next: l.closedAt
      ? { tip: 'Open it to read what was said. It is closed, so to ask again start a new chat.', instead: 'Anything it prepared is still where it was left: a draft, a plan, a task.' }
      : l.title === null
        ? { tip: 'There is nothing in this chat to come back to. Start a new one when you have something to ask.', instead: undefined }
        : { tip: `Open it to carry on where you left off with ${withWhom}. Nothing it prepared was sent; anything that went out, you sent.`, instead: 'Or start a new chat if this is about something different: a chat stays tied to what it was about.' },
  };
}

// --- who one may talk to ---------------------------------------------------------------------------

export function explainChatAgent(a: WireChatAgent, who: Who, v: Viewer, extra: { sponsor: Party | null }): ItemContext {
  const twin = a.kind === 'twin';
  const name = twin ? `${a.givenName}, your twin` : `${a.givenName} Agent`;
  const lead = twin ? null : who.lead(v.department);
  const ask = a.starters.length > 0 ? `Try: “${a.starters[0]}”` : twin ? 'Ask it to draft a reply, find a time with a colleague, or ask a department for something.' : 'Ask it about the work it does.';
  const next = ((): ItemContext['next'] => {
    if (a.state === 'not_provisioned') {
      return { tip: `${a.givenName} is declared in the ledger but has no account yet, so there is nobody to talk to. A superadmin provisions it; until then ask ${lead && lead.name !== 'You' ? lead.name : 'the department’s lead'} instead.`, instead: undefined };
    }
    if (a.state === 'stopped') {
      return { tip: `${a.givenName} is stopped, so it cannot answer. A superadmin restarts it; nothing it did earlier is lost.`, instead: 'Meanwhile its department’s lead can say where its work stands.' };
    }
    return twin
      ? { tip: `${ask} You read what it prepares and you send it; it never sends for you.`, instead: 'For something that belongs to a department’s work, ask that department’s agent from the buttons above instead.' }
      : { tip: `${ask} Anything it proposes waits on a person, up to what its department’s autonomy allows.`, instead: `For something personal to you, ask your twin instead: it works only in your own account.` };
  })();
  return {
    subject: { kind: twin ? 'twin' : 'agent', title: name },
    summary: twin
      ? `${a.givenName} is your twin: ${a.purpose ?? 'it keeps your diary, drafts your replies and asks departments for things on your behalf'}. It has no address of its own and it prepares; it never sends.`
      : `${a.givenName} is ${a.title}.${a.purpose ? ` ${a.purpose}` : ''}${a.role ? ` Its role: ${a.role.toLowerCase()}.` : ''}`,
    need: a.state === 'active' ? 'What you want done, in your own words.' : 'Nothing from you: it is not available.',
    why: twin ? 'Everyone has one, working only inside their own account.' : 'It belongs to the department you are working in, so it is offered here.',
    source: null,
    broughtBy: extra.sponsor,
    owner: twin ? who.me : lead,
    knows: others([extra.sponsor, lead]),
    facts: [
      ['What to ask', a.starters.length > 0 ? a.starters.slice(0, 2).map((s) => `“${s}”`).join(' · ') : null],
      ['State', a.state === 'active' ? null : a.state === 'stopped' ? 'Stopped' : 'Not set up yet'],
    ],
    next,
  };
}

// --- a twin's plan ---------------------------------------------------------------------------------------

type Plan = Extract<WireTwinPlanAnswer, { kind: 'plan' }>;
type Handoff = Extract<WireTwinPlanAnswer, { kind: 'handoff' }>;

export function explainTwinPlan(answer: Plan, extra: { twin: string; confirmed: boolean }, who: Who): ItemContext {
  const { plan, inventory } = answer;
  const waiting = inventory.filter((t) => t.status === 'needs_person').map((t) => toolName(t.toolId));
  const setup = inventory.filter((t) => t.status === 'unavailable').map((t) => toolName(t.toolId));
  const twin: Party = { name: extra.twin, kind: 'twin' };
  const next = ((): ItemContext['next'] => {
    if (extra.confirmed) return { tip: `Confirmed, so nothing more is needed from you. ${extra.twin} tells you when it is done; the task has its own page.`, instead: 'Changing your mind now is a new task, not an edit of this one.' };
    const base = plan.queries.length > 0 ? 'Read the exact words that go to the public web search, then press Confirm task: it runs this plan and nothing else.' : 'Read the tools and what you get back, then press Confirm task: it runs this plan and nothing else.';
    if (waiting.length > 0) return { tip: `${base} It will wait on ${waiting.join(' and ')}: a superadmin has to grant ${waiting.length === 1 ? 'it' : 'them'}, and is told when you confirm.`, instead: 'Or change the plan to use only tools that are ready, and it starts at once.' };
    if (setup.length > 0) return { tip: `${base} ${setup.join(' and ')} must be set up first, so the task waits on that.`, instead: 'Or change the plan so it does not need it.' };
    return { tip: base, instead: 'Not what you meant? Type what is different and press Change the plan: the twin writes a new version and nothing runs until you confirm.' };
  })();
  return {
    subject: { kind: 'plan', title: plan.title },
    summary: `${extra.twin} has planned “${plan.title}”: ${plan.subject}. You get back: ${plan.output}. ${extra.confirmed ? 'You confirmed it.' : 'Nothing has run.'}`,
    need: extra.confirmed ? 'Nothing.' : 'Your confirmation, or a change. A twin runs only a plan its person confirmed.',
    why: 'You asked for this in the chat. The twin plans before it does anything, and what it shows is the plan the platform stored, not its own description of it.',
    source: { label: 'Your request in the side chat' },
    broughtBy: who.me,
    owner: who.me,
    involved: [twin],
    knows: waiting.length > 0 ? [{ name: 'A superadmin', kind: 'person', role: 'grants the tools it waits on' }] : undefined,
    facts: [
      ['Tools', inventory.map((t) => toolName(t.toolId)).join(', ') || null],
      ['At most', plural(plan.maxCalls, 'call')],
      ['Web searches', plural(plan.queries.length, 'search', 'searches')],
      ['Documents it may read', (plan.materials ?? []).length > 0 ? String((plan.materials ?? []).length) : null],
      ['Waiting on', waiting.length > 0 ? waiting.join(', ') : null],
    ],
    next,
  };
}

export function explainHandoff(answer: Handoff, who: Who, done: boolean): ItemContext {
  const nia: Party = { name: 'Nia', kind: 'agent', role: 'researches for Executive' };
  return {
    subject: { kind: 'request', title: answer.party },
    summary: `${answer.party} is a company the platform holds as unapproved, so your twin does not research it. ${answer.why}`,
    need: done ? 'Nothing.' : 'Your press, to hand the ask to Nia in Executive as a request.',
    why: 'A twin works only with approved parties. An unapproved company goes to Executive’s own agent as a request, with your words sealed as its body.',
    source: { label: 'Your request in the side chat' },
    broughtBy: who.me,
    owner: done ? null : who.me,
    involved: [nia],
    knows: others(who.knowers('executive')),
    next: done
      ? { tip: 'Nothing more is needed. Nia has it and Executive tells you what it finds.', instead: 'Approving the company is a superadmin’s decision; until then it stays Executive’s to look into.' }
      : { tip: 'Press Ask Nia to send your words, sealed, as a request to Executive. Nothing is researched until Executive takes it on.', instead: 'Or leave it: if you need the company worked with directly, ask a superadmin to approve it first.' },
  };
}
